import qs from 'qs'

import {monolithGet, monolithPost} from './fetch'
import {camelCaseObjectKeys, onStatus, shimJson} from './helpers'

const DEFAULT_CHECKED_OUT_ORDERS_PARAMS = {
  exchange: 'False',
  home_try_on: 'False',
  order_completed: 'True',
  ordering: '-checked_out_on',
}

/**
 * Gets precheckout validation data for the designated order ID.
 *
 * @param {Number} orderId The order ID to validate against
 * @return {Promise} Resolves when the GET request has completed
 */
export const getPrecheckoutValidation = (orderId) =>
  monolithGet(`/v1/orders/${orderId}/precheckout-validation`).then(
    onStatus({OK: shimJson(camelCaseObjectKeys)}),
  )

/**
 * Returns an object that tells us whether the fit is guaranteed or not, based
 * on current datetime and the order's event date. We use this data to show
 * prompts to the user that their garment may not fit correctly.
 *
 * @param {Number} orderId The order ID to assess
 * @return {Promise} Resolves when GET request has completed
 */
export const getFitGuarantee = (orderId) =>
  monolithGet(`/v1/orders/${orderId}/fit-guarantee`).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )

/**
 * Retrieve calculated totals for a specific order
 *
 * @param {Object}  props
 * @param {Boolean} [props.applyAccountBalance]  Whether to apply the account balance to the totals
 * @param {Number}  props.orderId                The order ID to query
 */
export const getOrderSummary = ({applyAccountBalance = false, orderId}) => {
  const query = applyAccountBalance ? '?apply_account_balance=True' : ''
  return monolithGet(`/v1/orders/${orderId}/totals${query}`).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )
}

export const saveShippingAddress = (orderId, payload) =>
  monolithPost(`/v1/orders/${orderId}/shipping-address`, payload)

export const getShippingAddress = (orderId) => monolithGet(`/v1/orders/${orderId}/shipping-address`)

export const getCheckedOutOrders = (userId) =>
  monolithGet(
    `/v1/users/${userId}/orders${qs.stringify(DEFAULT_CHECKED_OUT_ORDERS_PARAMS, {
      addQueryPrefix: true,
    })}`,
  )

// This API is used to prepopulate the shipping address field.  If no shipping address is available from a previous
// session, we will try to fetch a shipping address from collection of previously checked out orders
export const getDefaultShippingAddress = (orderId, userId) =>
  Promise.all([getShippingAddress(orderId), getCheckedOutOrders(userId)])
export const saveBillingAddress = (userId, payload) =>
  monolithPost(`/v1/users/${userId}/billing-address`, payload)

/**
 * Tells the server to checkout for an order.
 *
 * @param {Number} orderId The order ID to process checkout for
 * @param {Object} payload The data about what the user wishes to pay for
 * @return {Promise} Resolves when the POST finishes
 */
export const completeCheckout = (orderId, payload) =>
  monolithPost(`/v1/orders/${orderId}/checkout`, payload).then(
    onStatus({DEFAULT: shimJson(camelCaseObjectKeys)}),
  )
